import React, { useEffect, useMemo, useRef, useState } from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Box, Stack, Typography } from "@mui/material";

import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import AlertInline from "@src/sharedComponents/AlertInline/AlertInline";

import styles from "./ProposalAlerts.module.css";

export const ALERT_MESSAGES = {
  manualTradingApex:
    "Manual trading is required for the mutual fund portion of Apex rebalancing proposals",
  tradingGroupModified:
    "Trading group was modified after this proposal was generated",
} as const;

const ProposalAlerts = ({ alerts }: { alerts: string[] }) => {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [isSeeMoreDisabled, setIsSeeMoreDisabled] = useState(false);

  const observer = useMemo(
    () =>
      new IntersectionObserver(
        (entries) => {
          const [entry] = entries;

          setIsSeeMoreDisabled(entry.isIntersecting);
        },
        {
          root: containerRef.current,
          rootMargin: "0px",
          threshold: 0.8,
        }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [containerRef.current]
  );

  useEffect(() => {
    const targetElement = targetRef.current;

    if (targetElement) {
      observer.observe(targetElement);
    }

    return () => {
      if (targetElement) {
        observer.unobserve(targetElement);
      }
    };
  }, [observer, targetRef]);

  return (
    <Box className={styles.container}>
      {alerts.length === 0 ? (
        <>
          <Typography className={styles.alertsCount}>No Alerts</Typography>
          <Box className={styles.noAlertsBox}>
            <Typography className={styles.noAlertsText}>
              No alerts associated with this proposal.
            </Typography>
          </Box>
        </>
      ) : (
        <>
          <Stack direction="row" justifyContent="space-between">
            <Typography className={styles.alertsCount}>
              {alerts.length} Alerts
            </Typography>
            {alerts.length > 4 && (
              <Box className={styles.seeMoreContainer}>
                <Typography
                  className={toClassName(styles.seeMoreText, {
                    [styles.seeMoreTextDisabled]: isSeeMoreDisabled,
                  })}
                >
                  Scroll to see more
                </Typography>
                <ArrowForwardIcon
                  className={toClassName(styles.seeMoreIcon, {
                    [styles.seeMoreIconDisabled]: isSeeMoreDisabled,
                  })}
                />
              </Box>
            )}
          </Stack>
          <Stack
            ref={containerRef}
            className={styles.alertsContainer}
            direction="row"
          >
            {alerts.map((alert, index) => (
              <AlertInline
                key={index}
                ref={index === alerts.length - 1 ? targetRef : null}
                className={styles.alert}
                severity="warning"
                message={alert}
              />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default ProposalAlerts;
